import { useState, useCallback, useMemo } from "react";
import BreakdownDialog from "./BreakdownDialog";

export const PAYMENT_DELAY_BREAKDOWN_COLUMNS = [
  { key: "invoice_number", label: "Invoice No." },
  { key: "currency", label: "Currency" },
  { key: "due_date", label: "Due Date", type: "date" },
  { key: "paid_date", label: "Paid Date", type: "date" },
  { key: "days_late", label: "Payment Status", type: "pill" },
];

function daysLateTone(days) {
  if (!Number.isFinite(days) || days === 0) return "green";
  // if (days <= 90) return "amber";
  return "red";
}

function buildPaymentDelayBreakdownRows(items) {
  if (!Array.isArray(items)) return [];
  return items.map((item, index) => {
    const days = typeof item?.days_late === "number" ? item.days_late : parseFloat(item?.days_late);
    const safeDays = Number.isFinite(days) ? days : 0;
    return {
      id: item?.invoice_number || index,
      invoice_number: item?.invoice_number || "-",
      currency: item?.currency || "-",
      due_date: item?.due_date,
      paid_date: item?.paid_date,
      days_late: { label: safeDays <= 0 ? "On Time" : `${safeDays} day${safeDays === 1 ? "" : "s"} late`, tone: daysLateTone(safeDays), },
      _paidPeriod: typeof item?.paid_date === "string" ? item.paid_date.slice(0, 7) : null,
    };
  });
}

function formatPeriodLabel(period) {
  if (!period || typeof period !== "string") return "";
  const [year, month] = period.split("-");
  if (!year || !month) return period;
  const date = new Date(Number(year), Number(month) - 1, 1);
  if (Number.isNaN(date.getTime())) return period;
  return new Intl.DateTimeFormat("en-US", { month: "long", year: "numeric" }).format(date);
}

// Drives the "Payment Delay Breakdown" modal for the payment_delay_history chart:
export function usePaymentDelayBreakdown({ invoiceBreakdown, chartItems, onTrack }) {
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const reset = useCallback(() => {
    setSelectedPeriod(null);
    setModalOpen(false);
  }, []);

  const allRows = useMemo(() => buildPaymentDelayBreakdownRows(invoiceBreakdown), [invoiceBreakdown]);
  const canRender = allRows.length > 0;
  const canFilterByPeriod = canRender && chartItems.length > 0;

  const rows = useMemo(() => {
    if (!selectedPeriod) return allRows;
    return allRows.filter((row) => row._paidPeriod === selectedPeriod);
  }, [allRows, selectedPeriod]);

  const handleChartClick = useCallback(
    (params) => {
      if (!params || params.componentType !== "series" || !canFilterByPeriod) return;
      const period = chartItems[params.dataIndex]?.period ?? null;
      setSelectedPeriod(period);
      setModalOpen(true);
      onTrack?.(`Graph Bar Clicked: ${period || ""}`);
    },
    [chartItems, canFilterByPeriod, onTrack],
  );

  const chartEvents = useMemo(() => ({ click: handleChartClick }), [handleChartClick]);

  const closeModal = useCallback(() => setModalOpen(false), []);

  const titleSuffix = useMemo(() => formatPeriodLabel(selectedPeriod), [selectedPeriod]);

  return { rows, canRender, canFilterByPeriod, chartEvents, modalOpen, closeModal, titleSuffix, reset };
}

export function PaymentDelayBreakdownModal({ open, onClose, rows, titleSuffix }) {
  const title = "Payment Delay Breakdown";
  const invoiceCount = Array.isArray(rows) ? rows.length : 0;
  const countLabel = `${invoiceCount} ${invoiceCount === 1 ? "Invoice" : "Invoices"}`;
  const subtitle = titleSuffix ? `${titleSuffix}  (${countLabel})` : countLabel;
  return (
    <BreakdownDialog
      open={open}
      onClose={onClose}
      title={title}
      subtitle={subtitle}
      exportFileName={titleSuffix ? `${title} - ${titleSuffix}` : title}
      columns={PAYMENT_DELAY_BREAKDOWN_COLUMNS}
      rows={rows}
    />
  );
}
